import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, IconButton, useTheme } from "@mui/material";

import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridValidRowModel,
  GridSortModel,
  GridEventListener,
} from "@mui/x-data-grid";
import { useNotification } from "@hooks/useNotifications";
import usePersistedState from "@hooks/usePersistedState";
import { Search } from "@components";
import type { IPageableList, IPageableRequest } from "types";
import { ArrowPathIcon } from "@heroicons/react/24/outline";

import { gridStyles, gridClickableRowStyles, barStyles } from "./styles";

const defaultFiltersState: IPageableRequest = {
  orderBy: "creationDate",
  descendingOrder: true,
  page: 0,
  pageSize: window.appConfig.list.pageSize,
};

export const PageableList = <T,>({
  columns,
  getService,
  storageId,
  disableColumnSorting = true,
  hasSearch,
  initialRequestParams = {},
  updateBreadcrumbs,
  isRowLink,
  onRowClick,
}: {
  columns: GridColDef[];
  getService: Function;
  storageId: string;
  disableColumnSorting?: boolean;
  hasSearch?: boolean;
  initialRequestParams?: IPageableRequest & any;
  updateBreadcrumbs?: (data: any) => void;
  isRowLink?: boolean;
  onRowClick?: GridEventListener<"rowClick">;
}) => {
  const [items, setItems] = useState<IPageableList<T>>();
  const [isLoading, setIsLoading] = useState(false);
  const { showError } = useNotification();
  const controllerRef = useRef<AbortController | null>();
  const navigate = useNavigate();
  const theme = useTheme();

  const sxStyles = useMemo(() => {
    const isRowClickable = Boolean(onRowClick);

    return {
      ...(typeof gridStyles === "function" ? gridStyles(theme) : gridStyles),
      ...(isRowClickable && typeof gridClickableRowStyles === "function" ? gridClickableRowStyles(theme) : {}),
    };
  }, []);

  const { state: requestParams, setState: setRequestParams } = usePersistedState<IPageableRequest>(
    storageId,
    {
      ...defaultFiltersState,
      ...initialRequestParams,
    },
    "session",
  );

  const abortRequest = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
    }
  };

  useEffect(() => {
    const loadList = async () => {
      setIsLoading(true);
      abortRequest();

      const controller = new AbortController();
      controllerRef.current = controller;
      try {
        const data = await getService(requestParams, controller);
        updateBreadcrumbs?.(data);
        setItems(data);
        setIsLoading(false);
      } catch (error: any) {
        setIsLoading(false);

        if (!controller?.signal.aborted) {
          if (error?.response?.status === 404) {
            navigate("/404");
          } else {
            showError(error.message);
          }
        }
      }
    };

    loadList();

    return () => {
      abortRequest();
    };
  }, [requestParams]);

  const setPaginationModel = (model: GridPaginationModel) => {
    setRequestParams((prev: any) => ({ ...prev, page: model.page, pageSize: model.pageSize }));
  };

  // Search query
  const [searchQuery, setSearchQuery] = useState<string>(requestParams.search ?? "");
  const onSearch = (query: string) => {
    setSearchQuery(query);
    setRequestParams((prev: any) => ({ ...prev, search: query }));
  };

  // Refresh the data
  const handleRefresh = () => setRequestParams((prev: any) => ({ ...prev }));

  const handleSortModelChange = useCallback((sortingModel: GridSortModel) => {
    const { field, sort } = sortingModel?.[0] || {};

    setRequestParams((prev: any) => ({ ...prev, orderBy: field, descendingOrder: sort === "desc" }));
  }, []);

  return (
    <div className={isRowLink ? "data-grid--row-as-link" : ""}>
      {hasSearch && (
        <Box sx={barStyles}>
          <Search initialQuery={searchQuery} onSearch={onSearch} maxWidth={250} />
          <Box sx={{ flexGrow: 1 }} />
          <Box>
            <IconButton onClick={handleRefresh}>
              <ArrowPathIcon width={18} height={18} />
            </IconButton>
          </Box>
        </Box>
      )}
      <DataGrid
        autoHeight
        sx={sxStyles}
        columns={columns}
        disableColumnMenu
        disableColumnSelector
        disableRowSelectionOnClick
        disableMultipleRowSelection
        disableColumnSorting={disableColumnSorting}
        onSortModelChange={handleSortModelChange}
        disableDensitySelector
        rows={(items?.list ?? []) as GridValidRowModel[]}
        pagination
        paginationMode="server"
        filterMode="server"
        sortingMode="server"
        sortingOrder={["asc", "desc"]}
        rowCount={items?.totalCount ?? 0}
        pageSizeOptions={window.appConfig.list.pageSizeOptions}
        paginationModel={{
          pageSize: requestParams.pageSize,
          page: requestParams.page,
        }}
        onPaginationModelChange={setPaginationModel}
        loading={isLoading}
        onRowClick={onRowClick}
        initialState={{
          pagination: { paginationModel: { pageSize: requestParams.pageSize } },
          sorting: {
            sortModel: [
              {
                field: requestParams.orderBy,
                sort: requestParams.descendingOrder ? "desc" : "asc",
              },
            ],
          },
        }}
      />
    </div>
  );
};
